import { useState } from "react";
import { toast } from "sonner";
import { Loader2, FileText, CheckCircle2, ArrowRight, ExternalLink, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePricing } from "@/lib/pricing";

const CANTONS = ["AG","AI","AR","BE","BL","BS","FR","GE","GL","GR","JU","LU","NE","NW","OW","SG","SH","SO","SZ","TG","TI","UR","VD","VS","ZG","ZH"];
const VAT = 1.081; // 8.1% CH MWST

const inputClass = "w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 outline-none focus:border-gold/50 focus:ring-2 focus:ring-gold/20 transition-all text-foreground placeholder:text-muted-foreground/50 text-sm";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

export default function InvoiceOrderDialog({ open, onOpenChange, quantity = 1, mode = "license", days = 30 }) {
  const { user, refreshUser } = useAuth();
  const pricing = usePricing();
  const isAi = mode === "ai_extension";
  const [form, setForm] = useState({
    company_name: "", contact_name: user?.name || "", email: user?.email || "",
    street: "", street_number: "", zip_code: "", city: "", state: "ZH",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const qty = isAi ? 1 : (Number(quantity) || 1);
  const teamPrice = pricing?.license?.team ?? 2900;
  const aiPrice = (pricing?.ai_extension?.team?.[days]) ?? 290;
  const net = isAi ? aiPrice : teamPrice * qty;
  const gross = net * VAT;

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e?.preventDefault();
    for (const [k, label] of [["company_name", "Firmenname"], ["email", "E-Mail"], ["street", "Strasse"], ["zip_code", "PLZ"], ["city", "Ort"]]) {
      if (!String(form[k] || "").trim()) { toast.error(`Bitte ${label} ausfüllen.`); return; }
    }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/invoice", {
        plan: "team", kind: mode, quantity: qty, days, billing: form,
      });
      setResult(data);
      await refreshUser().catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const close = (o) => { onOpenChange(o); if (!o) setResult(null); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-lg glass-strong border-white/10 max-h-[90vh] overflow-y-auto" data-testid="invoice-order-dialog">
        {!result ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <FileText className="h-5 w-5 text-gold" /> {isAi ? "KI-Coach verlängern – auf Rechnung (Team)" : "Team-Lizenz auf Rechnung"}
              </DialogTitle>
              <DialogDescription>
                {isAi
                  ? "Wir erstellen Ihnen eine Schweizer QR-Rechnung (zahlbar innert 14 Tagen netto). Der KI-Coach wird für alle registrierten Teammitglieder sofort verlängert."
                  : "Wir erstellen Ihnen eine Schweizer QR-Rechnung (zahlbar innert 14 Tagen netto). Ihr Vollzugang wird sofort freigeschaltet."}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3 mt-1">
              <Field label="Firmenname *">
                <input data-testid="invoice-company-name" className={inputClass} value={form.company_name} onChange={upd("company_name")} placeholder="Architekturbüro Muster AG" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Ansprechperson">
                  <input data-testid="invoice-contact-name" className={inputClass} value={form.contact_name} onChange={upd("contact_name")} placeholder="Vor- und Nachname" />
                </Field>
                <Field label="E-Mail *">
                  <input data-testid="invoice-email" type="email" className={inputClass} value={form.email} onChange={upd("email")} placeholder="rechnung@buero.ch" />
                </Field>
              </div>
              <div className="grid grid-cols-[1fr_90px] gap-3">
                <Field label="Strasse *">
                  <input data-testid="invoice-street" className={inputClass} value={form.street} onChange={upd("street")} placeholder="Musterstrasse" />
                </Field>
                <Field label="Nr.">
                  <input data-testid="invoice-street-number" className={inputClass} value={form.street_number} onChange={upd("street_number")} placeholder="12" />
                </Field>
              </div>
              <div className="grid grid-cols-[100px_1fr_90px] gap-3">
                <Field label="PLZ *">
                  <input data-testid="invoice-zip" className={inputClass} value={form.zip_code} onChange={upd("zip_code")} placeholder="8000" />
                </Field>
                <Field label="Ort *">
                  <input data-testid="invoice-city" className={inputClass} value={form.city} onChange={upd("city")} placeholder="Zürich" />
                </Field>
                <Field label="Kanton *">
                  <select data-testid="invoice-canton" className={inputClass} value={form.state} onChange={upd("state")}>
                    {CANTONS.map((c) => <option key={c} value={c} className="bg-[#0a0d12]">{c}</option>)}
                  </select>
                </Field>
              </div>

              <div className="rounded-xl bg-gold/10 border border-gold/25 p-3.5 flex items-end justify-between">
                <span className="text-xs uppercase tracking-widest text-muted-foreground">Rechnungsbetrag</span>
                <span className="font-heading font-extrabold text-xl text-gold" data-testid="invoice-amount-preview">
                  CHF {gross.toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground -mt-1">
                inkl. 8.1% MWST · {isAi ? `KI-Verlängerung ${days} Tage · ganzes Team` : `${qty} × Team-Lizenz`} · zahlbar innert 14 Tagen
              </p>
              {isAi && (
                <p className="text-[11px] text-muted-foreground -mt-1">Ein evtl. aktiver Willkommen-zurück-Rabatt wird auf der Rechnung automatisch berücksichtigt.</p>
              )}

              <button type="submit" disabled={loading} data-testid="invoice-submit-button"
                className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gold text-[#0a0d12] font-semibold hover:shadow-[0_8px_30px_-8px_rgba(212,175,55,0.7)] transition-all duration-300 disabled:opacity-60">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                {loading ? "Rechnung wird erstellt…" : isAi ? "Rechnung erstellen & Team verlängern" : "Rechnung erstellen & sofort freischalten"}
              </button>
              <p className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
                <ShieldCheck className="h-3.5 w-3.5 text-gold" /> QR-Rechnung via AbaNinja · Zahlungsziel 14 Tage
              </p>
            </form>
          </>
        ) : (
          <div data-testid="invoice-success" className="text-center py-2">
            <div className="relative mx-auto h-14 w-14 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-4">
              <CheckCircle2 className="h-7 w-7 text-emerald-400" />
            </div>
            <DialogTitle className="font-heading text-xl">{isAi ? "KI-Coach verlängert – Rechnung erstellt" : "Vollzugang aktiv – Rechnung erstellt"}</DialogTitle>
            <p className="text-foreground/70 mt-2 text-sm leading-relaxed">
              {isAi
                ? <>Der KI-Coach Ihres Teams ist wieder aktiv. Die QR-Rechnung haben wir an <strong>{form.email}</strong> gesendet (zahlbar innert 14 Tagen).</>
                : <>Ihr Team-Zugang ist freigeschaltet. Die QR-Rechnung haben wir an <strong>{form.email}</strong> gesendet (zahlbar innert 14 Tagen).</>}
            </p>
            <div className="mt-4 rounded-xl bg-white/5 border border-white/10 p-4 text-left text-sm space-y-1.5">
              <div className="flex justify-between"><span className="text-muted-foreground">Rechnung</span><span className="font-medium" data-testid="invoice-result-number">{result.invoice_number}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Betrag</span><span className="font-heading font-bold text-gold">CHF {result.amount?.toLocaleString("de-CH", { minimumFractionDigits: 2 })}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Zahlbar bis</span><span className="font-medium">{result.due_date}</span></div>
              {isAi
                ? <div className="flex justify-between"><span className="text-muted-foreground">KI aktiv bis</span><span className="font-medium">{result.ai_active_until}</span></div>
                : <div className="flex justify-between"><span className="text-muted-foreground">Lizenzschlüssel</span><span className="font-mono text-xs">{result.license_key}</span></div>}
            </div>
            <div className="mt-5 flex flex-col sm:flex-row gap-3 justify-center">
              {result.portal_url && (
                <a href={result.portal_url} target="_blank" rel="noreferrer" data-testid="invoice-portal-link"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full glass hover:glow-gold transition-all">
                  <ExternalLink className="h-4 w-4 text-gold" /> Rechnung ansehen
                </a>
              )}
              <Link to={isAi ? "/coach" : "/dashboard"} onClick={() => close(false)} data-testid="invoice-goto-course"
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-gold text-[#0a0d12] font-semibold hover:shadow-[0_8px_30px_-8px_rgba(212,175,55,0.7)] transition-all">
                {isAi ? "Zum KI-Coach" : "Zum Kurs"} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
